import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import AuthShell from "../components/auth/AuthShell";
import { checkAndApplyPendingRole, ensureUserProfileDocument } from "../lib/firebase";
import {
  AlertCircle, Eye, EyeOff, Loader,
} from "lucide-react";
import { isValidEmail, isValidPhone, normalizeEmail, normalizePhone } from "../utils/authValidation";
import { getFriendlyAuthError } from "../utils/authErrorMessages";
import { errorDev } from '../utils/logger.js';

// ─── Login Method Tabs ──────────────────────────────────────────
const LOGIN_METHODS = ["email", "username", "phone"];

const MAX_ATTEMPTS = 6;
const LOCK_WINDOW_MS = 10 * 60 * 1000;
const ATTEMPT_KEY = "orrith_login_attempts";

function getAttemptState() {
  try {
    const raw = window.localStorage.getItem(ATTEMPT_KEY);
    if (!raw) return { attempts: 0, lockedUntil: 0 };
    const parsed = JSON.parse(raw);
    return {
      attempts: Number(parsed.attempts) || 0,
      lockedUntil: Number(parsed.lockedUntil) || 0,
    };
  } catch (err) {
    errorDev("Operation failed:", err);
    return { attempts: 0, lockedUntil: 0 };
  }
}

function persistAttemptState(state) {
  try {
    window.localStorage.setItem(ATTEMPT_KEY, JSON.stringify(state));
  } catch (err) {
    // no-op when storage is unavailable
  errorDev("Operation failed:", err);
  }
}

function clearAttemptState() {
  try {
    window.localStorage.removeItem(ATTEMPT_KEY);
  } catch (err) {
    // no-op when storage is unavailable
  errorDev("Operation failed:", err);
  }
}

function Login() {
  const { t, ready: i18nReady } = useTranslation(["auth", "common"]);
  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback || key;
    try { const r = t(key); return r === key ? fallback : r; } catch (err) { errorDev("Operation failed:", err);  return fallback || key; }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const {
    user, loading, login, loginWithUsername, loginWithPhone,
    loginWithGoogle, error: authError,
  } = useAuth();

  // ── State ──────────────────────────────────────────────────────
  const [method, setMethod] = useState("email");
  const [identifier, setIdentifier] = useState("");   // email, username, or phone
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const redirectPath = new URLSearchParams(location.search).get("redirect") || "/farm";
  const authPromptKey = location.state?.authPromptKey || "";
  const authPrompt = location.state?.authPrompt || "";
  const localizedAuthPrompt = authPromptKey
    ? safeT(authPromptKey, authPrompt || safeT("auth.signInRequiredContinue", "Please log in or sign up to continue."))
    : authPrompt;
  const signupLink = `/signup?redirect=${encodeURIComponent(redirectPath)}`;

  // ── Already authenticated → redirect ──────────────────────────
  if (!loading && user) return <Navigate to={redirectPath} replace />;

  // ── Handlers ──────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    const attemptState = getAttemptState();
    const now = Date.now();
    if (attemptState.lockedUntil > now) {
      setError(safeT("auth.errors.tooManyAttempts", "Too many attempts. Please try again later."));
      return;
    }

    if (!identifier.trim() || !password.trim()) {
      setError(safeT("auth.errors.requiredFields", "Please fill in all required fields."));
      return;
    }

    if (method === "email" && !isValidEmail(identifier)) {
      setError(safeT("auth.errors.invalidEmail", "Please enter a valid email address."));
      return;
    }

    if (method === "phone" && !isValidPhone(identifier)) {
      setError(safeT("auth.errors.phoneRequired", "Enter a valid phone number with country code."));
      return;
    }

    try {
      setIsLoading(true);

      let loggedInUser;
      if (method === "email") {
        loggedInUser = await login(normalizeEmail(identifier), password);
      } else if (method === "username") {
        loggedInUser = await loginWithUsername(identifier.trim(), password);
      } else if (method === "phone") {
        loggedInUser = await loginWithPhone(normalizePhone(identifier), password);
      }

      // Auto-assign admin role if pre-provisioned
      if (loggedInUser) {
        await checkAndApplyPendingRole(loggedInUser.uid, loggedInUser.email);
      }

      clearAttemptState();

      navigate(redirectPath, { replace: true });
    } catch (err) {
      const current = getAttemptState();
      const attempts = current.attempts + 1;
      const lockedUntil = attempts >= MAX_ATTEMPTS ? Date.now() + LOCK_WINDOW_MS : 0;
      persistAttemptState({ attempts: lockedUntil ? 0 : attempts, lockedUntil });
      setError(
        authError ||
          getFriendlyAuthError(err, t, {
            defaultKey: "auth.errors.loginFailed",
            defaultMessage: "Sorry, we could not sign you in. Please check your details and try again.",
          })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    try {
      setIsLoading(true);
      const googleUser = await loginWithGoogle();
      await ensureUserProfileDocument(googleUser);
      // Auto-assign admin role if pre-provisioned
      if (googleUser) {
        await checkAndApplyPendingRole(googleUser.uid, googleUser.email);
      }
      navigate(redirectPath, { replace: true });
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        if (err.code === "auth/unauthorized-domain") {
          setError(safeT("auth.errors.unauthorizedDomain", "This domain is not authorized for Google sign-in."));
        } else if (err.code === "auth/popup-blocked") {
          setError(safeT("auth.errors.popupBlocked", "Popup was blocked by your browser. Allow popups and try again."));
        } else {
          setError(
            authError ||
              getFriendlyAuthError(err, t, {
                defaultKey: "auth.errors.loginFailed",
                defaultMessage: "Sorry, we could not sign you in. Please check your details and try again.",
              })
          );
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Loading spinner ───────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-neutral-900">
        <span className="orrith-loader orrith-loader-lg orrith-loader-primary" />
      </div>
    );
  }

  // ── Input type per method ───────────────────────
  const inputConfig = {
    email:    { type: "email", autoComplete: "email" },
    username: { type: "text", autoComplete: "username" },
    phone:    { type: "tel", autoComplete: "tel" },
  };
  const cfg = inputConfig[method];

  const methodLabel = {
    email:    safeT("auth.email", "Email Address"),
    username: safeT("auth.username", "Username"),
    phone:    safeT("auth.phoneNumber", "Phone Number"),
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <AuthShell
      title={safeT("auth.signIn", "Sign In")}
      subtitle={localizedAuthPrompt ? "" : safeT("auth.loginSubtitle", "Authenticate to continue in Orrith.")}
      helperLinks={[
        { to: "/forgot-password", label: safeT("auth.forgotPassword", "Forgot password?") },
        { to: signupLink, label: safeT("auth.signup", "Register") },
      ]}
    >
      <div>
        {localizedAuthPrompt && <InfoBanner message={localizedAuthPrompt} />}

        <div className="flex border-b border-gray-200 dark:border-neutral-700 mb-5">
          {LOGIN_METHODS.map((methodId) => (
            <button
              key={methodId}
              onClick={() => {
                setMethod(methodId);
                setError(null);
                setIdentifier("");
              }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors border-b-2 ${
                method === methodId
                  ? "border-green-700 text-green-700 dark:text-green-400 dark:border-green-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <span>
                {methodId === "email" && safeT("auth.loginWithEmail", "Email")}
                {methodId === "username" && safeT("auth.loginWithUsername", "Username")}
                {methodId === "phone" && safeT("auth.loginWithPhone", "Phone")}
              </span>
            </button>
          ))}
        </div>

        <form onSubmit={handleLogin} className="space-y-4" noValidate>
          <div>
            <label htmlFor="login-identifier" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {methodLabel[method]}
            </label>
            <input
              id="login-identifier"
              type={cfg.type}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete={cfg.autoComplete}
              aria-invalid={Boolean(error)}
              className="ui-input"
            />
          </div>

          <div>
            <label htmlFor="login-password" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
              {safeT("auth.password", "Password")}
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="ui-input ui-input-icon"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={showPassword ? safeT("auth.hidePassword", "Hide password") : safeT("auth.showPassword", "Show password")}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && <ErrorBanner message={error} />}

          <button
            type="submit"
            disabled={isLoading}
            className="ui-btn-primary w-full py-2.5"
          >
            {isLoading && <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" aria-hidden="true" />}
            {safeT("auth.signIn", "Sign In")}
          </button>
        </form>

        <div className="my-5 relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-neutral-700" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-2 bg-white dark:bg-neutral-900 text-gray-500 dark:text-gray-400">
              {safeT("auth.orContinueWith", "or continue with")}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="ui-btn-secondary w-full py-2.5"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {safeT("auth.continueWithGoogle", "Continue with Google")}
        </button>

        <p className="text-center text-gray-500 dark:text-gray-400 text-xs mt-3">
          {safeT("auth.termsAgreementPrefix", "By continuing, you acknowledge that you have read and agree to Orrith’s")}{" "}
          <Link to="/legal/terms" className="text-green-700 dark:text-green-400 hover:underline font-medium">
            {safeT("auth.rulesAndRegulations", "Rules & Regulations")}
          </Link>
          .
        </p>
      </div>
    </AuthShell>
  );
}

// ─── Reusable Error Banner ────────────────────────────────────
function ErrorBanner({ message }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span className="text-sm text-red-700 dark:text-red-300">{message}</span>
    </div>
  );
}

function InfoBanner({ message }) {
  return (
    <div className="mb-4 flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
      <AlertCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span className="text-sm text-green-700 dark:text-green-300">{message}</span>
    </div>
  );
}

export default Login;
